// Token provider function type - returns fresh token on each call (with internal caching)
// Optional invalidate method to force token refresh on next call
export type TokenProvider = (() => Promise<string>) & {
  invalidate?: () => void
}

export interface UploadTask {
  id: string
  name: string
  progress: number
  status: 'starting' | 'uploading' | 'pausing' | 'paused' | 'completed' | 'error' | 'pending'
  startTime: Date
  imageId?: number
  storeService?: string
  originalFile?: File
  originalDescription?: string
  speed?: number // bytes per second
  lastLoaded?: number // for speed calculation
  lastUpdateTime?: number // for speed calculation
}

export interface UploadContext {
  imageId: number
  storeServiceUrl: string
  getUploadToken: TokenProvider
  md5: string
  fileName: string
  fileSize: number
}

export interface ImageRegistrationResult {
  imageId: number
  storeServiceUrl: string
  getUploadToken: TokenProvider
  md5: string
  chunkList: Blob[]
}

export interface UploadChunkResponse {
  info?: {
    blkWritten: Record<string, boolean>
  }
}

export interface RequestOptions {
  initImagePartUploadFunc: (params: {
    file: File
    imageName: string
    description: string
    fileMd5: string
  }) => Promise<{ imageId: number; storeServiceUrl: string; getUploadToken: TokenProvider }>
  uploadPartImageFunc: (options: {
    chunk: Blob
    index: number
    fileMd5: string
    fileName: string
    fileSize: number
    imageId: number
    storeServiceUrl: string
    getUploadToken: TokenProvider
    signal?: AbortSignal
  }) => Promise<UploadChunkResponse>
  finishImagePartUploadFunc: (params: { imageId: number; fileMd5: string }) => Promise<any>
}

export function generateTaskId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Re-export UploadPausedError from shared upload utils
export { UploadPausedError } from '@/utils/upload'
